import React, { useState } from 'react';
import './OrchestrationIndicator.css';

/**
 * OrchestrationIndicator — Phase 2.
 *
 * Subtle gold marker placed next to a module-list row when Event Readiness
 * has open operations for that entity. Hover/tap reveals the operation titles
 * Milo is tracking; clicking the marker navigates back to Event Readiness with
 * the first open operation focused (Memory Halo on the queue row).
 *
 * Design vocabulary:
 *   - 9-10px gold dot, faint halo
 *   - small count beneath/beside (only when > 1)
 *   - no chip backgrounds, no enterprise badges, no productivity glyphs
 *   - 24×24 tap target invisible to the eye
 */
export default function OrchestrationIndicator({
  count = 0,
  titles = [],
  opIds = [],
  onNavigate,
  ariaLabel,
}) {
  const [open, setOpen] = useState(false);

  if (!count || count <= 0) return null;

  const label = ariaLabel || `${count} open operation${count === 1 ? '' : 's'} in Event Readiness`;

  const handleClick = (e) => {
    e.stopPropagation();
    if (typeof onNavigate === 'function' && opIds && opIds.length > 0) {
      onNavigate(opIds[0]);
    }
  };

  return (
    <span
      className={`oi${open ? ' oi-open' : ''}`}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
    >
      <button
        type="button"
        className="oi-btn"
        aria-label={label}
        aria-expanded={open}
        onClick={handleClick}
      >
        <span className="oi-dot" aria-hidden="true" />
        {count > 1 && <span className="oi-count" aria-hidden="true">{count}</span>}
      </button>
      {open && (
        <span className="oi-pop" role="tooltip">
          <span className="oi-pop-eyebrow">Milo is tracking</span>
          {titles && titles.length > 0 ? (
            <ul className="oi-pop-list">
              {titles.map((t, i) => (
                <li key={i} className="oi-pop-item">{t}</li>
              ))}
              {count > titles.length && (
                <li className="oi-pop-more">+ {count - titles.length} more</li>
              )}
            </ul>
          ) : (
            <span className="oi-pop-bare">{count} open in Event Readiness</span>
          )}
          <span className="oi-pop-cta">Tap to open in Event Readiness</span>
        </span>
      )}
    </span>
  );
}
